import type { GetMembersByWorkspaceIdQuery } from '@pique/graphql';
import { getMembersByWorkspaceIdQuery, Permission } from '@pique/graphql';
import { useMemo } from 'react';

import { useQuery } from '../use-query';

export function calculateWeight(member: Member) {
  const permissionWeight = {
    [Permission.Owner]: 4,
    [Permission.Admin]: 3,
    [Permission.Write]: 2,
    [Permission.Read]: 1,
  };

  const factors = [
    Number(member['permission'] === Permission.Owner),
    Number(!member['accepted']),
    permissionWeight[member['permission']] || 0,
  ];

  return factors.reduce((ret, factor, index, arr) => {
    return ret + factor * Math.pow(10, arr.length - 1 - index);
  }, 0);
}

export type Member = Omit<
  GetMembersByWorkspaceIdQuery['workspace']['members'][number],
  '__typename'
>;
export function useMembers(
  workspaceId: string,
  skip: number,
  take: number = 8
) {
  const { data } = useQuery({
    query: getMembersByWorkspaceIdQuery,
    variables: {
      workspaceId,
      skip,
      take,
    },
  });

  const members = data.workspace.members;

  return useMemo(() => {
    return members.sort((a: Member, b: Member) => {
      const weightDifference = calculateWeight(b) - calculateWeight(a);
      if (weightDifference !== 0) {
        return weightDifference;
      }
      if (a['name'] === null) return 1;
      if (b['name'] === null) return -1;
      return a['name'].localeCompare(b['name']);
    });
  }, [members]);
}
